import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { filter, first, forkJoin, mergeMap, Observable, of, switchMap } from 'rxjs';
import { Customer } from '../../models';
import { CustomPageConfigService } from '../../services/custom-page/custom-page-config.service';
import { CustomerService } from '../../services/customers/customer.service';
import { HomeNavigationConfigService } from '../../services/home-navigation-config/home-navigation-config.service';
import { User } from '../../state/auth/types';
import { BackendTemplate } from '../custom-page/custom-page-interface/template-component';
import { AuthService } from './../../services/auth/auth.service';
import { ConfigurationsService } from '../../services/configurations.service';
import { HomePageMatchGroups } from '../home-navigation-config/home-navigation-config.component';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';

@Component({
  selector: 'lib-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false
})
export class HomeComponent implements OnInit {
  protected router = inject(Router);
  protected authService = inject(AuthService);
  protected customerService = inject(CustomerService);
  protected homeNavigationConfigService = inject(HomeNavigationConfigService);
  protected customPageConfigService = inject(CustomPageConfigService);
  protected configurationService = inject(ConfigurationsService);
  protected gungFlowService = inject(GungFlowService);

  private readonly configurationId = 'HomePageConfiguration';

  ngOnInit() {
    this.initNavigation();
  }

  protected initNavigation(): void {
    this.configurationService.getConfigurationsByIds([this.configurationId]).pipe(
      first(),
      switchMap(confs => of(confs[0] || undefined))
    ).subscribe(homeConfiguration => {
      // Check if there are dynamic home links, if not then use legacy //
      if (homeConfiguration?.homePageMatchGroups && homeConfiguration?.homePageMatchGroups?.length > 0) {
        this.dynamicHomePageNavigation(homeConfiguration.homePageMatchGroups);
      } else {
        this.legacyHomePageNavigation();
      }
    });
  }

  protected legacyHomePageNavigation(): void {
    this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first(),
      mergeMap(user => {
        const requests: { [s: string]: Observable<any> } = { user: of(user) };
        // get customers only for users
        if (!(user.roles.includes('SALES') || user.roles.includes('ADMIN')) && user.roles.includes('USER')) {
          requests.customers = this.customerService.getCustomers().pipe(first());
        }
        if (this.homeNavigationConfigService.dynamicCustomPageByGroups) {
          requests.customPages = this.customPageConfigService.getTemplates().pipe(first());
        }
        requests.homeNavigationLoaded = this.homeNavigationConfigService.isLoaded().pipe(first());
        return forkJoin(requests);
      })
    ).subscribe(({ user, customers, customPages }) => {
      this.navigateTo(user, customers, customPages);
    });
  }

  navigateTo(user: User, customers?: Customer[], customPages?: BackendTemplate[]) {
    this.router.navigate(this.homeNavigationConfigService.getHomeLink(user, customers, customPages));
  }

  protected dynamicHomePageNavigation(homePageMatchGroups: HomePageMatchGroups[]): void {
    forkJoin({
      user: this.authService.getCurrentUser().pipe(filter(user => !!user), first()),
      userRoles: this.authService.getRoles().pipe(first()),
      selectedFlow: this.gungFlowService.getSelectedFlow().pipe(filter(flow => !!flow), first())
    }).subscribe(({ user, userRoles, selectedFlow }) => {
      const homeLink: string[] = this.homeNavigationConfigService.getDynamicHomeLink(homePageMatchGroups, user, userRoles, selectedFlow);

      // If there is no dynamic home link then defaulty to legacy //
      if (homeLink.length > 0) {
        this.router.navigate(homeLink);
      } else {
        this.legacyHomePageNavigation();
      }
    });
  }
}
